import React from 'react';
import { useFormContext } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';
import strings from 'strings';
import { FPActionButton, When } from 'UI/components/atoms';
import { BulkDrawerFormFieldsMap } from 'UI/components/organisms/BulkEmailForm/fields';
import { SvgAdd } from 'UI/res';
import { getOrdinalSuffix } from 'UI/utils';

import { MAX_STEPS_NUMBER, STEPS_ORDINAL_START } from '../../constants';
import { useBulkDrawerAttemptsHandlers } from '../../hooks/useBulkDrawerAttemptsHandlers';
import { useGetTriggerDays } from '../../hooks/useGetTriggerDays';
import { BulkEmailSectionsCollapser } from '../BulkEmailSectionsCollapser';

import { BulkAttemptForm } from './BulkAttemptForm';
import { useSequencesAttemptsFormStyles } from './BulkEmailMultipleAttemptsForm.styled';

const { sequences: sequencesCopies } = strings.bulkEmails.forms;

export const BulkEmailMultipleAttemptsForm = ({
  isFormBeingEdited,
  onFetchingSmartTags = () => {},
  shouldInitRecommendedDays = false,
  step = {}
}) => {
  const classes = useSequencesAttemptsFormStyles();
  const { currentStep } = step;

  const {
    attemptsIds,
    attemptsLength,
    handleAddAttempt,
    handleRemoveAttempt,
    shouldDisableAddStepButton
  } = useBulkDrawerAttemptsHandlers({ shouldAddSteps: !!currentStep });

  const { watch } = useFormContext();
  const scopeTitle = watch(BulkDrawerFormFieldsMap.BulkScope.key)?.title;
  const addButtonAttemptText = getOrdinalSuffix(
    (currentStep || STEPS_ORDINAL_START) + attemptsLength
  );
  const { recommendedDaysByStep } = useGetTriggerDays({
    scopeTitle,
    shouldInitRecommendedDays
  });
  const isLastStepAdded =
    attemptsLength >= MAX_STEPS_NUMBER ||
    currentStep - STEPS_ORDINAL_START + attemptsLength >= MAX_STEPS_NUMBER;

  return (
    <When condition={!!recommendedDaysByStep}>
      {attemptsIds.map((attemptId, index) => {
        const finalCurrentStep = (currentStep || STEPS_ORDINAL_START) + index;
        const shouldShowRemoveButton = attemptsLength > 1 || index !== 0;
        const isLastElement = attemptsLength - 1 === index;
        const ordinalSuffix = getOrdinalSuffix(finalCurrentStep);

        return (
          <BulkEmailSectionsCollapser
            key={attemptId}
            title={strings.formatString(sequencesCopies.sections.title, {
              attempt: ordinalSuffix
            })}
            shouldShowCollapseButton={!isFormBeingEdited}
            shouldShowBottomDivider={isLastElement}
            shouldShowTitle
            shouldShowTopDivider={(currentStep && index !== 0) || !currentStep}
            status={isFormBeingEdited ? step.status : null}
          >
            <BulkAttemptForm
              onFetchingSmartTags={onFetchingSmartTags}
              recommendedTriggerDays={recommendedDaysByStep}
              scopeTitle={scopeTitle}
              step={isFormBeingEdited ? step : { id: attemptId, currentStep: finalCurrentStep }}
            />
            <When condition={shouldShowRemoveButton}>
              <FPActionButton
                onClick={handleRemoveAttempt(attemptId)}
                text={sequencesCopies.buttons.removeStep}
                variant="text"
              />
            </When>
          </BulkEmailSectionsCollapser>
        );
      })}
      <When condition={!isLastStepAdded && !isFormBeingEdited}>
        <Grid container className={classes.buttonContainer}>
          <FPActionButton
            disabled={shouldDisableAddStepButton}
            icon={SvgAdd}
            iconPosition="left"
            iconProps={{ size: 'sm' }}
            onClick={handleAddAttempt}
            text={strings.formatString(sequencesCopies.buttons.addStep, {
              attempt: addButtonAttemptText
            })}
            variant="outlined"
          />
        </Grid>
      </When>
    </When>
  );
};
